import { Box, Typography, Chip } from '@mui/material';
import MenuItem from './MenuItem';

interface MenuContentProps {
  menuData: any;
  selectedCategory: string;
  selectedSubcategory: string;
  store: any;
  searchQuery?: string;
}

const MenuContent = ({
  menuData,
  selectedCategory,
  selectedSubcategory,
  store,
  searchQuery = '',
}: MenuContentProps) => {
  // Find the selected category
  const selectedCategoryData = menuData?.results?.find(
    (category: any) => category.title === selectedCategory
  );

  // Get items based on whether we have a subcategory selected
  let categoryItems = [];
  let displayTitle = selectedCategory;

  if (selectedSubcategory && selectedCategoryData?.subcategories?.length > 0) {
    // If subcategory is selected, get items from subcategory
    const selectedSubcategoryData = selectedCategoryData.subcategories.find(
      (subcategory: any) => subcategory.title === selectedSubcategory
    );
    categoryItems = selectedSubcategoryData?.items || [];
    displayTitle = selectedSubcategory;
  } else {
    // If no subcategory or category has no subcategories, get items from main category
    categoryItems = selectedCategoryData?.items || [];
  }

  // Get current day
  const currentDay = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
  });

  // Find today's timing from store data
  const todayTiming = store?.opening_hours?.find(
    (day: any) => day.day_of_week === currentDay
  );

  // Format time slots
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Generate timing display
  const getTimingDisplay = () => {
    if (!todayTiming?.is_open) {
      return 'Closed Today';
    }

    if (!todayTiming?.time_slots?.length) {
      return 'No timings available';
    }

    return todayTiming.time_slots
      .map(
        (slot: any) =>
          `${formatTime(slot.from_time)} - ${formatTime(slot.to_time)}`
      )
      .join(', ');
  };

  // Function to highlight search terms
  const highlightSearchTerm = (text: string) => {
    if (!searchQuery) return text;

    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{ backgroundColor: '#FFD700', fontWeight: 'bold' }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Box sx={{ flex: 1, width: '100%', overflowY: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          padding: '16px 24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
              letterSpacing: '-0.01em',
            }}
          >
            {highlightSearchTerm(
              displayTitle.toLowerCase().charAt(0).toUpperCase() +
                displayTitle.toLowerCase().slice(1)
            )}
          </Typography>
          {searchQuery && categoryItems.length > 0 && (
            <Chip
              label={`${categoryItems.length} result${categoryItems.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontSize: '10px',
                height: '20px',
                backgroundColor: '#E7F0FF',
                color: '#1976d2',
              }}
            />
          )}
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            fontSize: '12px',
            letterSpacing: '-0.01em',
            color: '#6B6F80',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '12px' }}>
            Timings :
          </Typography>
          {getTimingDisplay()}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
        {categoryItems?.length > 0 ? (
          categoryItems.map((item: any) => (
            <MenuItem key={item.title} item={item} searchQuery={searchQuery} />
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', mt: 4 }}
          >
            {searchQuery
              ? `No items found matching "${searchQuery}" in this category`
              : 'No items available in this category'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MenuContent;
